import type { CampaignWhatIfQuery, ListRunsQuery } from '@clarity/contract';

/**
 * Stable TanStack Query keys shared across the query hooks.
 *
 * Lives in its own leaf module (imported by both `./index.ts` and
 * `./subscriptions.ts`) so the subscription hooks can key off the same registry
 * without a `index ↔ subscriptions` import cycle — the barrel re-exports this so
 * callers still reach `queryKeys` from `../queries/index.js`.
 */
export const queryKeys = {
  clients: ['clients'] as const,
  client: (id: string) => ['clients', id] as const,
  platformFeeDefaults: ['settings', 'platform-fee-defaults'] as const,
  brands: ['brands'] as const,
  brandsByClient: (clientId: string) => ['brands', 'byClient', clientId] as const,
  stores: ['stores'] as const,
  store: (id: string) => ['stores', id] as const,
  storesByBrand: (brandId: string) => ['stores', 'byBrand', brandId] as const,
  // ── ingestion reads (clarity-onboarding-ingestion Req 14.2, 10.2) ──
  orders: (storeId: string) => ['stores', storeId, 'orders'] as const,
  order: (storeId: string, orderId: string) =>
    ['stores', storeId, 'orders', orderId] as const,
  statements: (storeId: string) => ['stores', storeId, 'statements'] as const,
  payouts: (storeId: string) => ['stores', storeId, 'payouts'] as const,
  onboardingCatalog: (storeId: string) =>
    ['stores', storeId, 'onboarding', 'catalog'] as const,
  // Deliberately NOT under the `onboardingCatalog` prefix: catalog save/edit
  // invalidations must not re-run the (slow, portal-backed) orders fetch.
  onboardingCatalogProvision: (storeId: string) =>
    ['stores', storeId, 'onboarding', 'catalog-provision'] as const,
  // The forced re-provision (provision=retry) — a SEPARATE key so it doesn't
  // collide with the `run` provision query's cache entry/fetcher.
  onboardingCatalogRetry: (storeId: string) =>
    ['stores', storeId, 'onboarding', 'catalog-retry'] as const,
  deriveProgress: (storeId: string) =>
    ['stores', storeId, 'onboarding', 'derive', 'progress'] as const,
  catalogProvisionProgress: (storeId: string) =>
    ['stores', storeId, 'onboarding', 'catalog', 'progress'] as const,
  derivedSummary: (storeId: string) =>
    ['stores', storeId, 'onboarding', 'derived'] as const,
  // ── MyThings owner-entered payouts + expenses (clarity-balance-coverage) ──
  mythingsPayouts: (storeId: string) => ['stores', storeId, 'mythings', 'payouts'] as const,
  mythingsExpenses: (storeId: string) => ['stores', storeId, 'mythings', 'expenses'] as const,
  // ── per-store analytics (clarity-owner-analytics) ──
  storeAnalytics: (storeId: string) => ['stores', storeId, 'analytics'] as const,
  customerSplit: (storeId: string, from: string, to: string) =>
    ['stores', storeId, 'analytics', 'customer-split', from, to] as const,
  dealIncrementality: (storeId: string, from: string, to: string) =>
    ['stores', storeId, 'analytics', 'deal-incrementality', from, to] as const,
  // ── onboarding campaign item-scope step (clarity-onboarding-campaign-scope Req 3.3) ──
  campaignScope: (storeId: string) =>
    ['stores', storeId, 'onboarding', 'campaign-scope'] as const,
  // ── reconciliation reads (clarity-reconciliation Req 8.4) ──
  reconciliationByPayout: (storeId: string, payoutId: string) =>
    ['stores', storeId, 'reconciliation', 'payout', payoutId] as const,
  reconciliationByPeriod: (storeId: string, periodStart: string, periodEnd: string) =>
    ['stores', storeId, 'reconciliation', 'period', periodStart, periodEnd] as const,
  // ── campaign reads (clarity-campaign-matching Req 9.1, 10.1) ──
  // All tenant-scoped to one store (the campaigns surface is per-store). A
  // deterministic what-if key folds the optional discountPct/cap/cpc overrides
  // so each what-if projection caches independently from the default detail.
  campaigns: (storeId: string) => ['stores', storeId, 'campaigns'] as const,
  campaign: (storeId: string, campaignId: string, whatIf?: CampaignWhatIfQuery) =>
    ['stores', storeId, 'campaigns', campaignId, whatIf ?? {}] as const,
  campaignOccurrence: (
    storeId: string,
    campaignId: string,
    occurrenceId: string,
  ) =>
    ['stores', storeId, 'campaigns', campaignId, 'occurrences', occurrenceId] as const,
  campaignReconciliation: (storeId: string, from?: string, to?: string) =>
    ['stores', storeId, 'campaigns', 'reconciliation', from ?? '', to ?? ''] as const,
  // ── terms axis + item-scoped deal economics (clarity-campaign-terms-economics Req 5) ──
  // Both mirror `campaignReconciliation`: tenant-scoped to one store, the
  // optional `from`/`to` folded into the key (default `''`) so each date-range
  // view caches independently. Economics is further keyed by `campaignId`.
  campaignTerms: (storeId: string, from?: string, to?: string) =>
    ['stores', storeId, 'campaigns', 'terms', from ?? '', to ?? ''] as const,
  // One terms bucket's DETAIL drill-down (clarity-campaign-terms-economics task
  // 9.3). Keyed by the bucket's signature `key` under the terms prefix, with the
  // optional range + pagination folded in (default `''`/0) so each date-range or
  // page caches independently — a "Load more" fetches a sibling page without
  // clobbering the first.
  termBucketDetail: (
    storeId: string,
    bucketKey: string,
    from?: string,
    to?: string,
    page?: number,
    pageSize?: number,
  ) =>
    [
      'stores',
      storeId,
      'campaigns',
      'terms',
      bucketKey,
      from ?? '',
      to ?? '',
      page ?? 0,
      pageSize ?? 0,
    ] as const,
  dealEconomics: (storeId: string, campaignId: string, from?: string, to?: string) =>
    ['stores', storeId, 'campaigns', campaignId, 'economics', from ?? '', to ?? ''] as const,
  // ── banner options (clarity-banner-options Req 4.1, 6.1) ──
  // The admin list is platform-wide (one key). The owner read is per-store; the
  // storeId sits in the middle, so admin writes invalidate every store's entry
  // with `isStoreBannerOptionsKey` rather than a prefix.
  bannerOptionsAdmin: () => ['admin', 'banner-options'] as const,
  storeBannerOptions: (storeId: string) => ['stores', storeId, 'banner-options'] as const,
  // ── locations (clarity-locations) — the one per-client admin view ──
  clientLocationsView: (clientId: string) => ['admin', 'clients', clientId, 'locations-view'] as const,
  // ── monthly campaign review (clarity-monthly-campaign-review) ──
  // The admin publish status is per (platform, month); the owner review + AM
  // brief reads are per (store, month). The publish mutation invalidates the
  // status key.
  monthlyReviewPublishStatus: (platformKey: string, month: string) =>
    ['admin', 'monthly-offer', platformKey, month, 'publish'] as const,
  monthlyReview: (storeId: string, month: string) =>
    ['stores', storeId, 'monthly-review', month] as const,
  monthlyReviewBrief: (storeId: string, month: string, locale?: string) =>
    ['stores', storeId, 'monthly-review', month, 'brief', locale ?? ''] as const,
  // ── advisor chat (clarity-advisor-chat) — the web thread for the client in
  // scope (a client's own tenant, or the client an admin has open) ──
  advisorThread: (clientId: string | null) => ['advisor', 'thread', clientId ?? ''] as const,
  // The caller's workspace AI usage this month vs its cap (clarity-advisor-llm).
  advisorUsage: (clientId: string | null) => ['advisor', 'usage', clientId ?? ''] as const,
  // Admin view of a workspace's AI usage + cap, keyed separately from the owner
  // read so an admin page and the owner meter don't collide (clarity-advisor-llm Req 4.1).
  advisorUsageAdmin: (clientId: string) => ['advisor', 'usage', 'admin', clientId] as const,
  // A workspace's user logins (clarity-workspace-users).
  workspaceUsers: (clientId: string) => ['clients', clientId, 'users'] as const,
  // ── admin ingestion control (admin-ingestion-control Req 5.2, 4.6) ──
  // A single stable key for the panel's run-history list. The optional filters
  // (storeId/batchId/status/limit) are folded into the key so a store- or
  // batch-scoped view caches independently; the panel's main list passes none.
  ingestionRuns: (filters?: ListRunsQuery) =>
    ['admin', 'ingestion', 'runs', filters ?? {}] as const,
  // ── subscription + billing reads (clarity-subscriptions Req 15.1, 15.5) ──
  // Keyed by the owning client; the mark-paid mutation invalidates BOTH so a
  // payment that clears a suspension also refreshes the subscription status.
  subscription: (clientId: string) => ['clients', clientId, 'subscription'] as const,
  invoices: (clientId: string) => ['clients', clientId, 'invoices'] as const,
};

/** True for any `['stores', <storeId>, 'banner-options']` key (any store). */
export function isStoreBannerOptionsKey(key: readonly unknown[]): boolean {
  return key.length === 3 && key[0] === 'stores' && key[2] === 'banner-options';
}
